"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Crown, Eye, Heart, MapPin, PanelRightOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { ServiceImageCarousel } from "@/components/home/ServiceImageCarousel";
import { ServiceQuickPreview } from "@/components/home/ServiceQuickPreview";
import { cn } from "@/lib/utils";
import {
  serviceCarouselImageUrls,
  serviceDetailPath,
  type MarketplaceService,
} from "@/lib/marketplace-services";
import clsx from "clsx";

export function ServiceCard({
  service,
  isFavorite,
  onToggleFavorite,
  layout = "grid",
  mobileViewMode,
}: {
  service: MarketplaceService;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  /** Row + thumbnail on small screens; from `lg` matches grid card (marketplace toggles are hidden). */
  layout?: "grid" | "list";
  mobileViewMode?: "grid-1" | "grid-2" | "list";
}) {
  const detailHref = serviceDetailPath(service);
  const isListMobile = layout === "list";
  const isTwoColumnsMobile = mobileViewMode === "grid-2";
  const locationLabels = service.locations
    .map((location) => (location.locationName ?? "").trim())
    .filter(Boolean);
  const firstLocationLabel = locationLabels[0];
  const extraLocationLabels = locationLabels.slice(1);
  const [showExtraLocations, setShowExtraLocations] = useState(false);
  const extraLocationsRef = useRef<HTMLDivElement>(null);
  const categoryNames = service.categories.map((category) => category.name).filter(Boolean);
  const firstCategoryName = categoryNames[0];
  const extraCategoryNames = categoryNames.slice(1);
  const [showExtraCategories, setShowExtraCategories] = useState(false);
  const extraCategoriesRef = useRef<HTMLDivElement>(null);
  const tooltipChipClassName =
    "rounded-[10px] bg-[#F4F4FF] px-2 py-0.5 text-[10px] w-fit font-medium text-primary shadow-sm sm:rounded-[14px] sm:px-3 h-[19px] mb-[2px] sm:py-1 sm:text-xs dark:bg-black/65 dark:text-white dark:ring-1 dark:ring-white/15";
  const tooltipPanelClassName =
    "absolute top-[calc(100%+8px)] z-0 w-max max-w-[min(220px,calc(100vw-1.5rem))] overflow-hidden rounded-xl border border-[#E3E7FA] bg-white/95 p-2.5 text-[11px] shadow-[0_14px_34px_rgba(28,38,82,0.18)] backdrop-blur-sm dark:border-white/15 dark:bg-[#1A1E2A]/95 dark:shadow-[0_14px_34px_rgba(0,0,0,0.45)]";
  const tooltipArrowClassName =
    "absolute -top-1 h-2.5 w-2.5 rotate-45 border-l border-t border-[#E3E7FA] bg-white/95 dark:border-white/15 dark:bg-[#1A1E2A]/95";
  const tooltipLabelClassName =
    "mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#637384] dark:text-white/70";
  const categoryTooltipItemClassName =
    "rounded-[10px] bg-[#F4F4FF] px-2 py-0.5 text-[10px] font-medium text-primary shadow-sm sm:rounded-[14px] sm:px-3 sm:py-1 sm:text-xs dark:bg-black/65 dark:text-white dark:ring-1 dark:ring-white/15";
  const locationTooltipItemClassName =
    "h-[28px] w-fit rounded-md bg-[#F7F8FF] p-2 text-[10px] font-medium text-[#000000] sm:h-[32px] dark:bg-white/12 dark:text-white";

  useEffect(() => {
    if (!showExtraLocations && !showExtraCategories) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        extraLocationsRef.current &&
        !extraLocationsRef.current.contains(target)
      ) {
        setShowExtraLocations(false);
      }
      if (
        extraCategoriesRef.current &&
        !extraCategoriesRef.current.contains(target)
      ) {
        setShowExtraCategories(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showExtraLocations, showExtraCategories]);

  return (
    <Card
      className={cn(
        "flex h-full flex-col gap-2 overflow-hidden rounded-[20px] border border-[#ECECFA] bg-card p-2 shadow-sm transition-shadow hover:shadow-md sm:rounded-[20px] sm:p-3 dark:border-white/10",
        service.highlighted && "premium-highlight-card",
        isTwoColumnsMobile && "max-lg:overflow-visible",
        isListMobile &&
          "max-lg:grid max-lg:grid-cols-[125px_minmax(0,1fr)] max-lg:items-stretch max-lg:gap-x-3 max-lg:gap-y-0"
      )}
    >
      <div
        className={cn(
          "relative min-h-0 w-full",
          isListMobile && "max-lg:h-full max-lg:min-h-[112px] max-lg:min-w-[125px]"
        )}
      >
        {service.highlighted && (
          <div
            className={cn(
              "pointer-events-none absolute bottom-2 right-2 top-auto z-30 inline-flex h-8 w-8 items-center justify-center rounded-full border border-blue-300/60 bg-gradient-to-br from-blue-100/95 via-sky-50/95 to-indigo-100/95 text-blue-700 shadow-[0_8px_18px_rgba(59,130,246,0.35)] backdrop-blur-sm sm:h-9 sm:w-9 dark:border-amber-300/25 dark:from-amber-500/20 dark:via-amber-400/15 dark:to-orange-500/20 dark:text-amber-100 lg:bottom-3 lg:right-3 lg:top-auto",
              isListMobile &&
                "max-lg:bottom-auto max-lg:left-2 max-lg:right-auto max-lg:top-2 sm:max-lg:left-3 lg:bottom-3 lg:right-3 lg:top-auto"
            )}
            title="Istaknuto"
            aria-label="Istaknuto"
          >
            <Crown className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2} />
          </div>
        )}
        <ServiceImageCarousel
          images={serviceCarouselImageUrls(service)}
          alt={service.title}
          maxImages={5}
          fillHeight={isListMobile}
          aspectClassName="aspect-[16/9]"
          className={cn(
            "w-full min-w-0 overflow-hidden rounded-[14px] sm:rounded-[18px]",
            isListMobile &&
              "h-full min-h-0 lg:aspect-[16/9] lg:h-auto lg:min-h-0 lg:rounded-[18px]"
          )}
        />
        {service.categories.length > 0 && (
          <div
            className={cn(
              "absolute left-2 top-2 flex max-w-full flex-wrap gap-1 sm:left-3 sm:top-3 sm:max-w-[78%] sm:gap-1.5",
              isListMobile && "max-lg:hidden"
            )}
          >
            {isTwoColumnsMobile && firstCategoryName ? (
              <>
                <div className="max-lg:flex max-lg:items-center max-lg:gap-1 lg:hidden">
                  <span className="rounded-[10px] bg-[#F4F4FF] px-2 py-0.5 text-[10px] font-medium text-primary shadow-sm sm:rounded-[14px] sm:px-3 sm:py-1 sm:text-xs dark:bg-black/65 dark:text-white dark:ring-1 dark:ring-white/15">
                    {firstCategoryName}
                  </span>
                  {extraCategoryNames.length > 0 && (
                    <div className="relative" ref={extraCategoriesRef}>
                      <button
                        type="button"
                        className={cn(tooltipChipClassName, "h-[22px] px-1.5")}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowExtraCategories((prev) => !prev);
                        }}
                        aria-label={`Prikaži još ${extraCategoryNames.length} kategorije`}
                      >
                        +{extraCategoryNames.length}
                      </button>

                      {showExtraCategories && (
                        <div className={cn(tooltipPanelClassName, "left-1/2 -translate-x-1/2")}>
                          <div className={cn(tooltipArrowClassName, "left-1/2 -translate-x-1/2")} />
                          <p className={tooltipLabelClassName}>
                            Ostale kategorije
                          </p>
                          <ul className="space-y-1.5">
                            {extraCategoryNames.map((name, idx) => (
                              <li
                                key={`${name}-${idx}`}
                                className={categoryTooltipItemClassName}
                              >
                                {name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:flex lg:max-w-full lg:flex-wrap lg:gap-1.5">
                  {service.categories.map((category) => (
                    <span
                      key={category.id}
                      className="rounded-[10px] bg-[#F4F4FF] px-2 py-0.5 text-[10px] font-medium text-primary shadow-sm sm:rounded-[14px] sm:px-3 sm:py-1 sm:text-xs dark:bg-black/65 dark:text-white dark:ring-1 dark:ring-white/15"
                    >
                      {category.name}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              service.categories.map((category) => (
                <span
                  key={category.id}
                  className="rounded-[10px] bg-[#F4F4FF] px-2 py-0.5 text-[10px] font-medium text-primary shadow-sm sm:rounded-[14px] sm:px-3 sm:py-1 sm:text-xs dark:bg-black/65 dark:text-white dark:ring-1 dark:ring-white/15"
                >
                  {category.name}
                </span>
              ))
            )}
          </div>
        )}
        <Button
          type="button"
          variant="secondary"
          size="icon"
          noScale
          className={cn(
            "absolute right-2 top-2 z-20 h-8 w-8 rounded-full bg-white/95 shadow-sm transition-colors hover:bg-white sm:right-3 sm:top-3 sm:h-9 sm:w-9 dark:bg-background/90 lg:right-3 lg:top-3",
            isListMobile &&
              "max-lg:right-2 max-lg:top-2 sm:max-lg:right-3 lg:right-3 lg:top-3",
            isFavorite
              ? "text-red-500 hover:text-red-500"
              : "text-foreground/70 hover:text-red-500"
          )}
          aria-label={isFavorite ? "Ukloni iz omiljenih" : "Dodaj u omiljene"}
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite();
          }}
        >
          <Heart
            className={cn(
              "h-5 w-5 transition-[fill,color]",
              isFavorite ? "fill-current" : "fill-transparent"
            )}
            strokeWidth={1.75}
          />
        </Button>
      </div>

      <CardContent
        className={cn(
          "flex flex-1 flex-col justify-between gap-1.5 px-0.5 pb-0.5 pt-1.5 sm:gap-2 sm:px-1 sm:pb-1 sm:pt-2",
          isListMobile &&
            "min-w-0 max-lg:justify-center max-lg:px-0 max-lg:pt-0 max-lg:gap-2 max-lg:py-0"
        )}
      >
        {isListMobile && service.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 lg:hidden">
            {service.categories.slice(0, 3).map((category) => (
              <span
                key={category.id}
                className="rounded-[10px] bg-muted/90 px-2 py-0.5 text-[10px] font-medium text-foreground shadow-sm dark:bg-white/12 dark:text-white"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
        {detailHref ? (
          <CardTitle
            className={cn(
              "line-clamp-2 text-sm font-semibold leading-tight tracking-tight sm:text-base",
              isTwoColumnsMobile && "max-lg:line-clamp-1"
            )}
          >
            <Link
              href={detailHref}
              className={clsx("line-clamp-2 transition-colors duration-200 hover:text-primary dark:hover:text-zinc-300", isTwoColumnsMobile && "max-lg:line-clamp-1")}
            >
              {service.title}
            </Link>
          </CardTitle>
        ) : (
          <CardTitle
            className={cn(
              "line-clamp-2 text-sm font-semibold leading-tight tracking-tight sm:text-base",
              isTwoColumnsMobile && "max-lg:line-clamp-1"
            )}
          >
            {service.title}
          </CardTitle>
        )}

        {service.locations.length > 0 && (
          <>
            {isTwoColumnsMobile && firstLocationLabel ? (
              <>
                <div className="max-lg:flex max-lg:flex-wrap max-lg:items-center max-lg:gap-1.5 lg:hidden">
                  <div className="h-[28px] w-fit rounded-md bg-[#F7F8FF] p-2 text-[10px] font-medium text-[#000000] sm:h-[32px] dark:bg-white/12 dark:text-white">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-[#2648FF] dark:text-[#7F93FF]" />
                      {firstLocationLabel}
                    </span>
                  </div>

                  {extraLocationLabels.length > 0 && (
                    <div className="relative" ref={extraLocationsRef}>
                      <button
                        type="button"
                        className={cn(tooltipChipClassName, "h-[28px] px-2 dark:bg-white/12 dark:text-white")}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowExtraLocations((prev) => !prev);
                        }}
                        aria-label={`Prikaži još ${extraLocationLabels.length} lokacije`}
                      >
                        +{extraLocationLabels.length}
                      </button>

                      {showExtraLocations && (
                        <div className={cn(tooltipPanelClassName, "right-0")}>
                          <div className={cn(tooltipArrowClassName, "right-4")} />
                          <p className={tooltipLabelClassName}>
                            Ostale lokacije
                          </p>
                          <ul className="space-y-1.5">
                            {extraLocationLabels.map((label, idx) => (
                              <li
                                key={`${label}-${idx}`}
                                className={locationTooltipItemClassName}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  <MapPin className="h-3.5 w-3.5 text-[#2648FF] dark:text-[#7F93FF]" />
                                  {label}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="hidden lg:flex lg:flex-wrap lg:gap-2">
                  {service.locations.map((location) => (
                    <div
                      key={location.id}
                      className="h-[28px] w-fit rounded-md bg-[#F7F8FF] p-2 text-[10px] font-medium text-[#000000] sm:h-[32px] dark:bg-white/12 dark:text-white"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5 text-[#2648FF] dark:text-[#7F93FF]" />
                        {(location.locationName ?? "").trim()}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div
                className={cn("flex flex-wrap gap-2", isListMobile && "max-lg:gap-1.5")}
              >
                {service.locations.map((location) => (
                  <div
                    key={location.id}
                    className="h-[28px] w-fit rounded-md bg-[#F7F8FF] p-2 text-[10px] font-medium text-[#000000] sm:h-[32px] dark:bg-white/12 dark:text-white"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-[#2648FF] dark:text-[#7F93FF]" />
                      {(location.locationName ?? "").trim()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex h-[34px] items-center gap-1.5 pt-1.5 sm:h-[36px] sm:gap-2 sm:pt-2">
          {detailHref && (
            <Button
              variant="outline"
              className="h-8 max-h-[34px] flex-1 !rounded-[8px] border-[#ECECFA] px-2 text-[12px] font-semibold leading-[150%] sm:h-11 sm:max-h-[36px] sm:rounded-[8px] sm:text-base dark:border-white/15"
              asChild
            >
              <Link
                href={detailHref}
                className="inline-flex items-center rounded-[8px] justify-center !text-[12px] lg:!text-[14px] leading-[150%] py-2 px-4"
              >
                Detalji
              </Link>
            </Button>
          )}
          <ServiceQuickPreview
            service={service}
            trigger={
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8 max-h-[34px] max-w-[34px] rounded-[8px] border-[#ECECFA] sm:h-11 sm:w-11 sm:max-h-[36px] sm:max-w-[36px] sm:rounded-[8px] dark:border-white/15"
                aria-label="Brzi pregled"
              >
                <Eye className="h-4 w-4" strokeWidth={1.9} />
              </Button>
            }
          />
        </div>
      </CardContent>
    </Card>
  );
}
